// TAD-5644 — Replacement for moment-business-days.
//
// moment-business-days is a 4+ year unmaintained CommonJS package that
// monkey-patches moment.fn at load time. The pattern doesn't survive Vite's
// ESM bundling (the patches land on a different moment instance than the
// one consumers import) and was blocking the CRA → Vite migration.
//
// The app uses only three things from moment-business-days:
//   - moment().businessAdd(N)._d  — add N business days, return Date
//   - momentA.businessDiff(momentB) — count business days between two moments
//   - moment.updateLocale('us', { workingWeekdays: [1,2,3,4,5] }) — Mon-Fri
//
// No holidays are configured. Business days = Mon-Fri. The two helpers below
// preserve that contract and accept native Date inputs (callers do .toDate()
// on their moments before passing in) so this util has no moment dependency.

const isWeekend = (date) => {
  const dow = date.getDay();
  return dow === 0 || dow === 6; // Sunday = 0, Saturday = 6
};

/**
 * Add `days` business days (Mon-Fri) to `start`. Returns a new Date.
 * Equivalent to moment-business-days `moment(start).businessAdd(days)._d`.
 *
 * @param {Date} start — origin date (not mutated)
 * @param {number} days — non-negative integer count of business days to add
 * @returns {Date}
 */
export function businessAdd(start, days) {
  const result = new Date(start.getTime());
  let remaining = Math.max(0, Math.floor(days));
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result)) {
      remaining -= 1;
    }
  }
  return result;
}

/**
 * Absolute count of business days (Mon-Fri) between two dates. Days are
 * counted as the number of weekday boundaries crossed when walking from the
 * earlier date to the later one. Same-day → 0. Order-independent (matches
 * moment-business-days's absolute-value behavior).
 *
 * @param {Date} a
 * @param {Date} b
 * @returns {number}
 */
export function businessDiff(a, b) {
  if (!a || !b) return 0;
  const earlier = a <= b ? a : b;
  const later = a <= b ? b : a;
  // Normalize to midnight so partial days don't double-count.
  const cursor = new Date(earlier.getFullYear(), earlier.getMonth(), earlier.getDate());
  const target = new Date(later.getFullYear(), later.getMonth(), later.getDate());
  let count = 0;
  while (cursor < target) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekend(cursor)) {
      count += 1;
    }
  }
  return count;
}
